import React, { useState, useEffect, useRef } from 'react';
import { Filter, Search, X, Check, CheckSquare, XSquare, Trash2 } from 'lucide-react';
import IconButton from './IconButton';

const ColumnHeaderFilter = ({ 
  column, 
  title, 
  options = [], 
  selectedValues, 
  onFilterChange 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Se selectedValues estiver vazio ou null, consideramos que todos estão selecionados (comportamento padrão de não filtro),
  // mas para a UI de checkboxes, precisamos saber quais marcar.
  // Se estiver vazio, na verdade significa "sem filtro", então visualmente "todos marcados" é o estado inicial lógico se formos filtrar.
  // Mas a prop selectedValues vira com os itens FILTRADOS. Se vier vazia, pode significar ou "nenhum" ou "todos" dependendo da logica do pai.
  // Vamos assumir: selectedValues null ou vazio significa "sem filtro ativo", logo todos os checkboxes devem começar marcados.
  
  // Melhor abordagem: O pai passa selectedValues. Se for null/undefined, significa sem filtro.
  
  const [tempSelected, setTempSelected] = useState([]);
  const dropdownRef = useRef(null);

  const isFiltered = selectedValues !== null && selectedValues !== undefined;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Inicializa tempSelected com o que está valendo ou todos se não houver filtro
      if (selectedValues !== null && selectedValues !== undefined) {
        setTempSelected(selectedValues);
      } else {
        setTempSelected(options);
      }
      setSearchTerm('');
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, selectedValues, options]);

  const filteredOptions = options.filter(opt => 
    String(opt).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectAll = () => {
    const newSelected = new Set(tempSelected);
    filteredOptions.forEach(opt => newSelected.add(opt));
    setTempSelected(Array.from(newSelected));
  };

  const handleDeselectAll = () => {
    setTempSelected([]);
  };

  const handleSelect = (val) => {
    if (tempSelected.includes(val)) {
      setTempSelected(tempSelected.filter(v => v !== val));
    } else {
      setTempSelected([...tempSelected, val]);
    }
  };

  const handleApply = () => {
    // Se selecionou todos os disponíveis, é o mesmo que limpar o filtro
    if (tempSelected.length === options.length) {
      onFilterChange(null);
    } else {
      onFilterChange(tempSelected);
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    onFilterChange(null);
    setIsOpen(false);
  };

  return (
    <div className="relative flex items-center justify-between group" ref={dropdownRef}>
      <span>{title}</span>
      <IconButton
        icon={Filter}
        onClick={() => setIsOpen(!isOpen)}
        variant={isFiltered ? 'primary' : 'secondary'}
        size="sm"
        title="Filtrar"
        className="ml-2"
      />

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-50 flex flex-col">
          <div className="p-3 border-b border-gray-100">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
              <span>Mostrando {filteredOptions.length}</span>
              <div className="flex gap-2">
                 <IconButton icon={CheckSquare} onClick={handleSelectAll} variant="secondary" size="sm" title="Selecionar todos" />
                 <IconButton icon={XSquare} onClick={handleDeselectAll} variant="secondary" size="sm" title="Desmarcar todos" />
                 <IconButton icon={Trash2} onClick={handleClear} variant="danger" size="sm" title="Limpar filtro" />
              </div>
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="Pesquisar..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
              <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto p-1">
            {filteredOptions.length === 0 ? (
               <div className="p-3 text-center text-sm text-gray-500">Nenhum item encontrado</div>
            ) : (
                filteredOptions.map((opt, idx) => (
                <label key={idx} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer rounded">
                    <input
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={tempSelected.includes(opt)}
                    onChange={() => handleSelect(opt)}
                    />
                    <span className="ml-2 text-sm text-gray-700 truncate" title={opt}>{opt === '' ? '(Vazio)' : opt}</span>
                </label>
                ))
            )}
          </div>

          <div className="p-3 border-t border-gray-100 flex justify-end gap-2 bg-gray-50 rounded-b-lg">
            <IconButton
              icon={X}
              onClick={() => setIsOpen(false)}
              variant="secondary"
              size="sm"
              title="Cancelar"
            />
            <IconButton
              icon={Check}
              onClick={handleApply}
              variant="success"
              size="sm"
              title="Aplicar filtro"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnHeaderFilter;
